import {
  createSellOrder,
  CreateSellOrderProps,
  getSellOrder,
  getSellOrders,
  GetSellOrdersProps,
  updateSellOrder,
  UpdateSellOrderProps,
} from "@/services/sell-order.service";
import { Dispatch, SetStateAction } from "react";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { getSellOrderFilter, SOFRProps } from "./filter-obj";
import { NavigateFunction } from "react-router-dom";
import { formatCurrencyInputToNumber } from "@/utils/query-string";
import { AppRoutes } from "@/constants/routes";
import { toaster } from "@/components/ui/toaster";
import { AxiosError } from "axios";
import { SellOrderDetail, SellOrderEntity } from "./sell-order.schema";
import { handlePermissionError } from "@/utils/permission-check";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { EstadosOrdenVenta } from "@/constants/enums/status/entity-status-ids";

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => Promise<void>;
  getValues: UseFormGetValues<FieldValues>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
}

export const clearFiltersHandler = async ({
  setFiltersApplied,
  setValue,
  getValues,
  submitFilters,
  setOpenFilters,
}: ClearFiltersHandlerProps) => {
  setValue("order_date", undefined);
  setValue("account_owner", "");
  setValue("order_amount", "");
  setValue("estados", []);
  setValue("ton", "");
  setValue("grain", []);

  submitFilters?.(getValues());
  setFiltersApplied(false);
  setOpenFilters(false);
};

interface SubmitFiltersHandlerProps {
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<SetStateAction<{}>>;
  fetchSellOrders: (filters?: any) => Promise<void>;
  event: FieldValues;
}

export const submitFiltersHandler = async ({
  event,
  setCurrentFilters,
  setFiltersApplied,
  fetchSellOrders,
  setOpenFilters,
}: SubmitFiltersHandlerProps) => {
  setFiltersApplied(true);

  const filterObj: Partial<GetSellOrdersProps> = getSellOrderFilter(
    event as SOFRProps,
  );

  setCurrentFilters(filterObj);

  if (!Object.keys(filterObj)?.length) setFiltersApplied(false);

  await fetchSellOrders(filterObj);

  setOpenFilters(false);
};

export {
  getOptionsHandler,
  loadOptionsHandler,
  type GetAccountOptionsHandlerProps,
  type LoadOptionsHandlerProps,
} from "@/utils/account-options-handler";

export interface CreateSellOrderHandlerProps {
  navigate: NavigateFunction;
  setLoadingCreateSellOrder: Dispatch<SetStateAction<boolean>>;
  watchedValues: any;
  fetchPermissions: () => Promise<void>;
}

export const createSellOrderHandler = async ({
  navigate,
  setLoadingCreateSellOrder,
  fetchPermissions,
  watchedValues,
}: CreateSellOrderHandlerProps) => {
  setLoadingCreateSellOrder(true);

  try {
    const parsedTon =
      typeof watchedValues?.ton === "string"
        ? Number.parseFloat(watchedValues.ton.replace(",", "."))
        : watchedValues?.ton;

    const objToSend: CreateSellOrderProps = {
      nroCuentaOrigen: watchedValues?.account?.value,
      tipoFijacion: watchedValues?.fixation_type?.[0],
      grano: watchedValues?.grain,
      toneladas: parsedTon,
      cosecha: watchedValues?.harvest?.[0],
      monto: formatCurrencyInputToNumber(watchedValues?.price ?? 0),
      esCanje: watchedValues?.isExchange,
      mensaje: watchedValues?.order_detail,
    };

    await createSellOrder(objToSend);

    toaster.create({
      title: "Orden de venta creada con éxito",
      type: "success",
    });
    setLoadingCreateSellOrder(false);
    navigate(AppRoutes.SELL_ORDERS);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string; errors?: string }>;

    const errorMsj = errorObj?.response?.data?.errors;

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.SELL_ORDERS,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: errorMsj ?? "No se ha podido crear la solicitud.",
        duration: 6000,
      });
    }
    throw error;
  } finally {
    setLoadingCreateSellOrder(false);
  }
};

export interface UpdateSellOrderAmountHandlerProps {
  errors: any;
  setLoadingEditAmount: Dispatch<SetStateAction<boolean>>;
  sellOrder: any;
  fetchSellOrder: (showLoad: boolean) => Promise<void>;
  setIsEditingAmount: Dispatch<SetStateAction<boolean>>;
  watchedValues: any;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const updateSellOrderAmountHandler = async ({
  errors,
  setLoadingEditAmount,
  sellOrder,
  fetchSellOrder,
  setIsEditingAmount,
  navigate,
  fetchPermissions,
  watchedValues,
}: UpdateSellOrderAmountHandlerProps) => {
  if (errors?.sell_order_amount) return;
  try {
    setLoadingEditAmount(true);

    await updateSellOrder({
      idSolicitud: sellOrder?.id ?? 0,
      monto: formatCurrencyInputToNumber(watchedValues?.sell_order_amount),
    });

    await fetchSellOrder(false);

    setLoadingEditAmount(false);
    setIsEditingAmount((prev) => !prev);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (handled) setIsEditingAmount(false);
    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo editar el monto solicitado.",
      });
    }
    throw error;
  } finally {
    setLoadingEditAmount(false);
  }
};

export interface HandleRejectSellOrderHandlerProps {
  event?: FieldValues;
  setLoadingRejectSellOrder: Dispatch<SetStateAction<boolean>>;
  sellOrder?: SellOrderDetail | null;
  navigate: NavigateFunction;
  setOpenRejectModal: Dispatch<SetStateAction<boolean>>;
  fetchPermissions: () => Promise<void>;
}
export const handleRejectSellOrderHandler = async ({
  setLoadingRejectSellOrder,
  event,
  sellOrder,
  navigate,
  setOpenRejectModal,
  fetchPermissions,
}: HandleRejectSellOrderHandlerProps) => {
  setLoadingRejectSellOrder(true);
  try {
    const objToSend: UpdateSellOrderProps = {
      idSolicitud: Number(sellOrder?.id),
      mensaje: event?.request_detail,
      estado: EstadosOrdenVenta.Rechazada,
    };

    await updateSellOrder(objToSend);

    toaster.create({
      type: "success",
      title: `Orden de venta rechazada exitosamente.`,
    });

    setLoadingRejectSellOrder(false);

    navigate(AppRoutes.SELL_ORDERS);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string; errors?: string }>;

    const errorMsj = errorObj?.response?.data?.errors;

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: errorMsj ?? "No se pudo rechazar la orden de venta.",
      });
      throw error;
    }
  } finally {
    setLoadingRejectSellOrder(false);
  }

  setOpenRejectModal(false);
};

export interface HandleExecuteSellOrderProps {
  setLoadingExecuteSellOrder: Dispatch<SetStateAction<boolean>>;
  sellOrder?: SellOrderDetail | null;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  watchedValues?: FieldValues
}

export const handleExecuteSellOrderHandler = async ({
  setLoadingExecuteSellOrder,
  navigate,
  sellOrder,
  fetchPermissions,
  watchedValues,
}: HandleExecuteSellOrderProps) => {
  setLoadingExecuteSellOrder(true);
  try {
    const objToSend: UpdateSellOrderProps = {
      idSolicitud: sellOrder?.id || 0,
      estado: EstadosOrdenVenta.Confirmada,
      precioFinal: formatCurrencyInputToNumber(watchedValues?.final_price)
    };

    await updateSellOrder(objToSend);

    toaster.create({
      type: "success",
      title: `Orden de venta realizada exitosamente.`,
    });

    setLoadingExecuteSellOrder(false);

    navigate(AppRoutes.SELL_ORDERS);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string; errors?: string }>;

    const errorMsj = errorObj?.response?.data?.errors;

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: errorMsj ?? "No se pudo realizar la orden de venta.",
      });
    }
    throw error;
  } finally {
    setLoadingExecuteSellOrder(false);
  }
};

export interface HandleSendSellOrderNegotiationHandlerProps {
  setLoadingSendNegotiation: Dispatch<SetStateAction<boolean>>;
  sellOrder?: SellOrderDetail | null;
  fetchSellOrder: (showLoad?: boolean) => Promise<void>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const sendSellOrderNegotiationHandler = async ({
  setLoadingSendNegotiation,
  sellOrder,
  fetchSellOrder,
  navigate,
  fetchPermissions,
}: HandleSendSellOrderNegotiationHandlerProps) => {
  setLoadingSendNegotiation(true);
  try {
    const objToSend: UpdateSellOrderProps = {
      idSolicitud: sellOrder?.id ?? 0,
      estado: EstadosOrdenVenta.Negociacion,
    };

    await updateSellOrder(objToSend);

    toaster.create({
      type: "success",
      title: "Orden de venta enviada a negociación.",
    });

    await fetchSellOrder(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string; errors?: string }>;
    const errorMsj = errorObj?.response?.data?.errors;

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: errorMsj ?? "No se pudo enviar la orden de venta a negociación.",
      });
    }
    throw error;
  } finally {
    setLoadingSendNegotiation(false);
  }
};

export interface HandleSendSellOrderCommunicationHandlerProps {
  setLoadingSendCommunication: Dispatch<SetStateAction<boolean>>;
  event?: FieldValues;
  sellOrder?: SellOrderDetail | null;
  fetchSellOrder: (showLoad?: boolean) => Promise<void>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  setOpenCommunicationModal?: Dispatch<SetStateAction<boolean>>;
}

export const sendSellOrderCommunicationHandler = async ({
  setLoadingSendCommunication,
  event,
  sellOrder,
  fetchSellOrder,
  navigate,
  fetchPermissions,
  setOpenCommunicationModal,
}: HandleSendSellOrderCommunicationHandlerProps): Promise<boolean> => {
  setLoadingSendCommunication(true);
  try {
    const objToSend: UpdateSellOrderProps = {
      idSolicitud: sellOrder?.id ?? 0,
      estado: EstadosOrdenVenta.EnComunicacion,
      mensaje: event?.request_detail,
    };

    await updateSellOrder(objToSend);

    toaster.create({
      type: "success",
      title: "Comunicado realizado exitosamente",
    });

    await fetchSellOrder(false);

    setOpenCommunicationModal?.(false);

    return true;
  } catch (error) {
    const errorMessage = getAxiosErrorMessage(
      error,
      "No se pudo enviar el comunicado.",
    );

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: errorMessage
      });
    }

    return false;
  } finally {
    setLoadingSendCommunication(false);
  }
};

interface FetchSellOrdersHandlerProps {
  setLoadingSellOrders: Dispatch<SetStateAction<boolean>>;
  filters?: GetSellOrdersProps;
  showLoad?: boolean;
  setSellOrders: Dispatch<
    SetStateAction<{
      listado: SellOrderEntity[];
      totalPages: number;
      totalRecords: number;
    }>
  >;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchSellOrdersHandler = async ({
  setLoadingSellOrders,
  filters,
  showLoad,
  setSellOrders,
  navigate,
  fetchPermissions,
}: FetchSellOrdersHandlerProps) => {
  try {
    if (showLoad) setLoadingSellOrders(true);

    const response = await getSellOrders({
      ...filters,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": 10,
    });

    setSellOrders(response);

    setLoadingSellOrders(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudo obtener las ordenes de venta.",
      });
    }
    throw error;
  } finally {
    setLoadingSellOrders(false);
  }
};

export interface FetchSellOrderDetailProps {
  id?: string;
  showLoad: boolean;
  setLoadingSellOrder: Dispatch<SetStateAction<boolean>>;
  setSellOrder: Dispatch<SetStateAction<SellOrderDetail | null | undefined>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchSellOrderHandler = async ({
  id,
  setLoadingSellOrder,
  showLoad,
  setSellOrder,
  fetchPermissions,
  navigate,
}: FetchSellOrderDetailProps) => {
  try {
    if (showLoad) setLoadingSellOrder(true);

    const response = await getSellOrder({
      id: Number(id),
    });

    setSellOrder(response);

    setLoadingSellOrder(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudo obtener la orden de venta.",
      });
    }
    throw error;
  } finally {
    setLoadingSellOrder(false);
  }
};
